import words from 'lodash/words'

import { textCollator } from '@app/utils/sort'

import { publisherOptions, sortOptions } from './constants'
import type {
  QueryRow,
  VisibilityFilter,
  PublisherFilter,
  SortOption,
} from './types'

const ANY: VisibilityFilter = 'any'

export const compareStringsAsc = (a: string, b: string): number =>
  textCollator.compare(a.toLowerCase(), b.toLowerCase())

export const compareByNameAsc = (a: QueryRow, b: QueryRow): number =>
  compareStringsAsc(a.name, b.name)

export const matchesSearch = (segment: QueryRow, term: string): boolean => {
  if (term.length === 0) return true
  const nameWords = words(segment.name.toLowerCase())
  // Each word of the search term must prefix-match some word in the name, so
  // a multi-word search like "end fin" still matches "End of Financial Year".
  return words(term).every((termWord) =>
    nameWords.some((nameWord) => nameWord.startsWith(termWord))
  )
}

export const matchesVisibility = (
  segment: QueryRow,
  filter: VisibilityFilter
): boolean => {
  if (filter === ANY) return true
  return segment.visibility === filter
}

export const matchesPublisher = (
  segment: QueryRow,
  filter: PublisherFilter
): boolean => {
  if (filter === publisherOptions.ALL) return true
  return segment.publisher === filter
}

export const sortSegments = (
  list: QueryRow[],
  option: SortOption
): QueryRow[] => {
  if (option === sortOptions.ALPHABETICALLY) {
    return [...list].sort(compareByNameAsc)
  }

  if (option === sortOptions.DATE_ADDED) {
    return [...list].sort((a, b) => {
      const dateA = a.created_at ? Date.parse(a.created_at) : 0
      const dateB = b.created_at ? Date.parse(b.created_at) : 0
      return dateA - dateB
    })
  }

  if (option === sortOptions.LAST_VISITED) {
    return [...list].sort((a, b) => {
      const dateA = a.last_visited_at ? Date.parse(a.last_visited_at) : 0
      const dateB = b.last_visited_at ? Date.parse(b.last_visited_at) : 0
      return dateB - dateA
    })
  }

  // default: recommended first (A-Z), then rest (A-Z)
  const { recommended, rest } = list.reduce<{
    recommended: QueryRow[]
    rest: QueryRow[]
  }>(
    (acc, s) => {
      if (s.recommended) acc.recommended.push(s)
      else acc.rest.push(s)
      return acc
    },
    { recommended: [], rest: [] }
  )
  return [...recommended.sort(compareByNameAsc), ...rest.sort(compareByNameAsc)]
}
